// pages/concept/concept.component.jsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, Outlet } from "react-router-dom";
import { io } from "socket.io-client";

import { IoSyncCircleOutline, IoSyncCircleSharp } from "react-icons/io5";

import { db, fieldValue } from "../../firebase/firebase.utils";

import {
  Excalidraw,
  MainMenu,
  Sidebar,
  Footer,
  restoreElements,
  getSceneVersion,
} from "@excalidraw/excalidraw";

import InvitetoPremium from "../../components/marketing/invite-to-premium.component";
import AddButton from "../../components/task-add-btn/AddButton";

import "./concept.styles.scss";

const COLLAB_URL = "https://antlogy-collab-server-production.up.railway.app";

const WHITEBOARD_TYPE = "whiteboard";
const CURSOR_THROTTLE_MS = 50;

/* -------------------------------------------------------------------------
   1. Build Library Items
------------------------------------------------------------------------- */

function getTaskLibraryItems(stationTasks = []) {
  return stationTasks.map((task, index) => {
    const uniqueSeed = Date.now() + index;

    return {
      id: `task-item-${task.id || index}-${uniqueSeed}`,
      status: "unpublished",

      elements: [
        {
          id: `container-${task.id}`,
          type: "rectangle",
          version: 1,
          versionNonce: 1,
          isDeleted: false,

          width: task.title.length * 10 + 20,
          height: 50,

          x: 0,
          y: 0,

          strokeColor: "#000000",
          backgroundColor: "#FFF8DC",
          fillStyle: "solid",

          strokeWidth: 1,
          strokeStyle: "solid",
          roughness: 1,
          opacity: 100,

          seed: uniqueSeed,
          groupIds: [],
          strokeSharpness: "sharp",

          boundElements: [
            {
              type: "text",
              id: `title-${task.id}`,
            },
          ],

          updated: Date.now(),
          angle: 0,
        },

        {
          id: `title-${task.id}`,
          type: "text",
          version: 1,
          versionNonce: 1,
          isDeleted: false,

          x: 10,
          y: 10,

          width: task.title.length * 10,
          height: 20,
          angle: 0,

          strokeColor: "#000000",
          backgroundColor: "transparent",
          fillStyle: "solid",

          strokeWidth: 1,
          strokeStyle: "solid",
          roughness: 0,
          opacity: 100,

          seed: uniqueSeed + 1,
          groupIds: [],
          strokeSharpness: "sharp",
          boundElements: [],

          updated: Date.now(),

          text: task.title || "Task",
          fontSize: 16,
          fontFamily: 1,
          textAlign: "center",
          verticalAlign: "middle",
          baseline: 16,

          containerId: `container-${task.id}`,
        },
      ],
    };
  });
}

/* -------------------------------------------------------------------------
   2. ConceptView
------------------------------------------------------------------------- */

const ConceptView = ({ station, stationTasks = [], isActive = true }) => {
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);

  const socketRef = useRef(null);
  const collaboratorsRef = useRef(new Map());

  // Prevent remote scene -> onChange -> socket -> remote scene loops.
  const applyingRemoteRef = useRef(false);

  // Same idea used by Excalidraw collaboration: don't broadcast the
  // same scene version repeatedly.
  const lastSceneVersionRef = useRef(-1);

  // Lets Firestore act as fallback when no live scene exists.
  const receivedLiveSceneRef = useRef(false);

  const lastCursorUpdateRef = useRef(0);

  // Redux / Router.
  const theme = useSelector((state) => state.theme.theme);
  const currentUser = useSelector((state) => state.user.currentUser);
  const userEntitlement = useSelector((state) => state.user.entitlement);

  const sidebarOpen = currentUser?.open;

  const location = useLocation();
  const currentSpaceId = location.pathname.split("/")[2];
  const currentStationId = location.pathname.split("/")[4];

  const [whiteboardDocId, setWhiteboardDocId] = useState(null);
  const [showInvitation, setShowInvitation] = useState(false);

  const roomId =
    currentSpaceId && currentStationId && whiteboardDocId
      ? `${currentSpaceId}:${currentStationId}:${whiteboardDocId}`
      : null;

  // Modes.
  const [liveMode, setLiveMode] = useState(station?.liveMode === true);

  const [zenMode, setZenMode] = useState(false);
  const [excaliTheme, setExcaliTheme] = useState("light");
  const [docked, setDocked] = useState(false);

  /* -----------------------------------------------------------------------
     Theme
  ----------------------------------------------------------------------- */

  useEffect(() => {
    setExcaliTheme(theme === "dark-theme" ? "dark" : "light");
  }, [theme]);

  /* -----------------------------------------------------------------------
     Live Mode state already comes from parent station listener
  ----------------------------------------------------------------------- */

  useEffect(() => {
    if (typeof station?.liveMode === "boolean") {
      setLiveMode(station.liveMode);
      setZenMode(false);
    }
  }, [station?.liveMode]);

  /* -----------------------------------------------------------------------
     Find / create the whiteboard Firestore document
  ----------------------------------------------------------------------- */

  useEffect(() => {
    if (!currentSpaceId || !currentStationId) return;

    let active = true;

    const conceptsRef = db
      .collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(currentStationId)
      .collection("concepts");

    const findOrCreateWhiteboard = async () => {
      try {
        const snap = await conceptsRef
          .where("type", "==", WHITEBOARD_TYPE)
          .limit(1)
          .get();

        if (!active) return;

        if (!snap.empty) {
          setWhiteboardDocId(snap.docs[0].id);
          return;
        }

        const newDocRef = await conceptsRef.add({
          title: "Whiteboard",
          type: WHITEBOARD_TYPE,
          elements: JSON.stringify([]),
          createdAt: fieldValue.serverTimestamp(),
          lastUpdatedBy: null,
        });

        if (active) {
          setWhiteboardDocId(newDocRef.id);
        }
      } catch (err) {
        console.error("Error creating whiteboard:", err);
      }
    };

    findOrCreateWhiteboard();

    return () => {
      active = false;
    };
  }, [currentSpaceId, currentStationId]);

  /* -----------------------------------------------------------------------
     Firestore persisted scene

     Firestore remains persistence / fallback.
     During Live Mode, a scene received from Socket.IO has priority.
  ----------------------------------------------------------------------- */

  useEffect(() => {
    if (
      !whiteboardDocId ||
      !excalidrawAPI ||
      !currentSpaceId ||
      !currentStationId
    ) {
      return;
    }

    const docRef = db
      .collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(currentStationId)
      .collection("concepts")
      .doc(whiteboardDocId);

    const unsub = docRef.onSnapshot((snap) => {
      if (!snap.exists) return;

      // Live socket scene is newer than persisted Firestore scene.
      if (liveMode && receivedLiveSceneRef.current) {
        return;
      }

      const data = snap.data() || {};

      // Ignore our own Save echo unless the canvas is still empty.
      if (
        data.lastUpdatedBy === currentUser?.uid &&
        excalidrawAPI.getSceneElements().length > 0
      ) {
        return;
      }

      let elements = [];

      try {
        elements = data.elements
          ? restoreElements(JSON.parse(data.elements))
          : [];
      } catch (err) {
        console.error("Failed to restore whiteboard:", err);
        return;
      }

      const scene = {
        elements,
      };

      if (data.appState) {
        scene.appState = data.appState;
      }

      excalidrawAPI.updateScene(scene);
    });

    return () => unsub();
  }, [
    whiteboardDocId,
    excalidrawAPI,
    currentSpaceId,
    currentStationId,
    currentUser?.uid,
    liveMode,
  ]);

  /* -----------------------------------------------------------------------
     Socket.IO Live Collaboration
  ----------------------------------------------------------------------- */

  useEffect(() => {
    if (
      !isActive ||
      !liveMode ||
      !roomId ||
      !currentUser?.uid ||
      !excalidrawAPI
    ) {
      return;
    }

    receivedLiveSceneRef.current = false;
    lastSceneVersionRef.current = -1;

    const socket = io(COLLAB_URL, {
      transports: ["websocket"],
    });

    socketRef.current = socket;

    const joinRoom = () => {
      socket.emit("join-board", {
        roomId,
        userId: currentUser.uid,
      });
    };

    const handleSceneUpdate = ({ elements }) => {
      if (!Array.isArray(elements)) return;

      receivedLiveSceneRef.current = true;

      let restored;

      try {
        restored = restoreElements(elements);
      } catch (err) {
        console.error("Failed to restore live scene:", err);
        return;
      }

      /*
       * Mark this version before updateScene().
       * updateScene may itself trigger onChange, and we don't want to
       * send the remote scene straight back to Railway.
       */
      lastSceneVersionRef.current = getSceneVersion(restored);

      applyingRemoteRef.current = true;

      try {
        excalidrawAPI.updateScene({
          elements: restored,
          commitToHistory: false,
        });

        // Account for any normalization done by Excalidraw.
        lastSceneVersionRef.current = getSceneVersion(
          excalidrawAPI.getSceneElements(),
        );
      } finally {
        // updateScene/onChange is normally synchronous, while this
        // protects against an immediate callback too.
        setTimeout(() => {
          applyingRemoteRef.current = false;
        }, 0);
      }
    };

    const handleCursorUpdate = ({
      userId,
      username,
      avatarUrl,
      pointer,
      button,
      selectedElementIds,
    }) => {
      if (!userId || userId === currentUser.uid || !pointer) {
        return;
      }

      const collaborators = new Map(collaboratorsRef.current);

      const existing = collaborators.get(userId) || {};

      collaborators.set(userId, {
        ...existing,
        pointer,
        button,
        username: username || "User",
        avatarUrl: avatarUrl || existing.avatarUrl || null,
        selectedElementIds: selectedElementIds || {},
      });

      collaboratorsRef.current = collaborators;

      excalidrawAPI.updateScene({
        collaborators,
      });
    };

    const handleCursorRemove = ({ userId }) => {
      if (!userId) return;

      const collaborators = new Map(collaboratorsRef.current);

      collaborators.delete(userId);

      collaboratorsRef.current = collaborators;

      excalidrawAPI.updateScene({
        collaborators,
      });
    };

    // Rejoin automatically after Socket.IO reconnects.
    socket.on("connect", joinRoom);

    socket.on("scene-update", handleSceneUpdate);

    socket.on("cursor-update", handleCursorUpdate);

    socket.on("cursor-remove", handleCursorRemove);

    return () => {
      socket.emit("leave-board", {
        roomId,
        userId: currentUser.uid,
      });

      socket.disconnect();

      if (socketRef.current === socket) {
        socketRef.current = null;
      }

      collaboratorsRef.current = new Map();

      excalidrawAPI.updateScene({
        collaborators: new Map(),
      });

      receivedLiveSceneRef.current = false;
      applyingRemoteRef.current = false;
      lastSceneVersionRef.current = -1;
    };
  }, [isActive, liveMode, roomId, currentUser?.uid, excalidrawAPI]);

  /* -----------------------------------------------------------------------
     Live scene changes
  ----------------------------------------------------------------------- */

  const onChangeLive = useCallback(
    (elements) => {
      const socket = socketRef.current;

      if (
        !isActive ||
        !liveMode ||
        !roomId ||
        !socket ||
        applyingRemoteRef.current
      ) {
        return;
      }

      const version = getSceneVersion(elements);

      // onChange also runs for app-state changes such as tool selection,
      // collaborators, etc. Only send when actual elements changed.
      if (version <= lastSceneVersionRef.current) {
        return;
      }

      lastSceneVersionRef.current = version;

      socket.emit("scene-update", {
        roomId,
        elements,
      });
    },
    [isActive, liveMode, roomId],
  );

  const onChangeDefault = useCallback(() => {}, []);

  /* -----------------------------------------------------------------------
     Excalidraw-native pointer collaboration
  ----------------------------------------------------------------------- */

  const handlePointerUpdate = useCallback(
    (payload) => {
      const socket = socketRef.current;

      if (
        !isActive ||
        !liveMode ||
        !roomId ||
        !socket ||
        !currentUser?.uid ||
        !payload?.pointer
      ) {
        return;
      }

      // Ignore multitouch / gesture pointer broadcasting.
      if (payload.pointersMap?.size >= 2) {
        return;
      }

      const now = Date.now();

      if (now - lastCursorUpdateRef.current < CURSOR_THROTTLE_MS) {
        return;
      }

      lastCursorUpdateRef.current = now;

      const selectedElementIds =
        excalidrawAPI?.getAppState()?.selectedElementIds || {};

      /*
       * Cursor packets are disposable.
       * volatile means Socket.IO can drop an old cursor packet instead
       * of queueing it behind newer packets.
       */
      socket.volatile.emit("cursor-update", {
        roomId,
        userId: currentUser.uid,
        username: currentUser?.userName || "User",
        avatarUrl: currentUser?.avatarTinyUrl || null,
        pointer: payload.pointer,
        button: payload.button,
        selectedElementIds,
      });
    },
    [
      isActive,
      liveMode,
      roomId,
      currentUser?.uid,
      currentUser?.userName,
      currentUser?.avatarTinyUrl,
      excalidrawAPI,
    ],
  );

  /* -----------------------------------------------------------------------
     Manual Save -> Firestore
  ----------------------------------------------------------------------- */

  const onSaveWhiteBoard = useCallback(() => {
    if (
      !excalidrawAPI ||
      !whiteboardDocId ||
      !currentSpaceId ||
      !currentStationId
    ) {
      return;
    }

    const docRef = db
      .collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(currentStationId)
      .collection("concepts")
      .doc(whiteboardDocId);

    const elements = excalidrawAPI.getSceneElements();

    docRef
      .update({
        elements: JSON.stringify(elements),
        lastUpdatedBy: currentUser?.uid || null,
      })
      .catch((err) => {
        console.error("Whiteboard save failed:", err);
      });
  }, [
    excalidrawAPI,
    whiteboardDocId,
    currentSpaceId,
    currentStationId,
    currentUser?.uid,
  ]);

  /* -----------------------------------------------------------------------
     Existing canvas refresh when this tab becomes visible
  ----------------------------------------------------------------------- */

  useEffect(() => {
    if (!isActive) return;

    const refresh = () => {
      excalidrawAPI?.refresh?.();
    };

    const frame = requestAnimationFrame(refresh);

    window.addEventListener("resize", refresh);

    return () => {
      cancelAnimationFrame(frame);

      window.removeEventListener("resize", refresh);
    };
  }, [isActive, sidebarOpen, excalidrawAPI]);

  /* -----------------------------------------------------------------------
     Scroll to content on Live Mode initiation
  ----------------------------------------------------------------------- */

  useEffect(() => {
    if (
      liveMode &&
      excalidrawAPI &&
      typeof excalidrawAPI.scrollToContent === "function"
    ) {
      excalidrawAPI.scrollToContent();
    }
  }, [liveMode, excalidrawAPI]);

  /* -----------------------------------------------------------------------
     Live Mode toggle
  ----------------------------------------------------------------------- */

  const handleSwitchtoLiveMode = useCallback(() => {
    if (!currentSpaceId || !currentStationId) {
      return;
    }

    const stationRef = db
      .collection("space")
      .doc(currentSpaceId)
      .collection("stations")
      .doc(currentStationId);

    stationRef
      .update({
        liveMode: !liveMode,
      })
      .catch((err) => {
        console.error("Live mode update failed:", err);
      });
  }, [currentSpaceId, currentStationId, liveMode]);

  const handleCheckEntitlement = useCallback(() => {
    if (userEntitlement === "free") {
      setShowInvitation(true);
      return;
    }

    if (userEntitlement === "antPro" || userEntitlement === "antBusiness") {
      handleSwitchtoLiveMode();
    }
  }, [userEntitlement, handleSwitchtoLiveMode]);

  /* -----------------------------------------------------------------------
     Task library
  ----------------------------------------------------------------------- */

  useEffect(() => {
    if (!excalidrawAPI) return;

    if (stationTasks.length > 0) {
      const items = getTaskLibraryItems(stationTasks);

      excalidrawAPI.updateLibrary({
        libraryItems: items,
      });

      excalidrawAPI.updateScene({
        appState: {
          isLibraryOpen: true,
        },
      });
    }
  }, [excalidrawAPI, stationTasks]);

  /* -----------------------------------------------------------------------
     Render
  ----------------------------------------------------------------------- */

  return (
    <div
      className="conceptView"
      style={{
        paddingLeft: sidebarOpen ? 0 : 15,
        display: isActive ? undefined : "none",
      }}
    >
      <div className="conceptContainer">
        <div className="concept_container">
          <Excalidraw
            excalidrawAPI={(api) => setExcalidrawAPI(api)}
            theme={excaliTheme}
            isCollaborating={liveMode}
            handleKeyboardGlobally={false}
            onChange={liveMode ? onChangeLive : onChangeDefault}
            onPointerUpdate={liveMode ? handlePointerUpdate : undefined}
            name="Custom Whiteboard"
            zenModeEnabled={zenMode}
            initialData={{
              libraryItems: [],
              appState: {
                isLibraryOpen: false,
              },
            }}
            renderTopRightUI={() => (
              <button
                style={{
                  background: excaliTheme === "dark" ? "black" : "white",

                  border:
                    excaliTheme === "dark"
                      ? "1px solid rgb(46, 46, 46)"
                      : "1px solid #cccccc",

                  borderRadius: "0.375rem",

                  color: excaliTheme === "dark" ? "#8f8f8f" : "#3d3d3d",

                  padding: "4px 10px",
                  fontWeight: "bold",
                }}
                onClick={onSaveWhiteBoard}
              >
                Save
              </button>
            )}
            UIOptions={{
              dockedSidebarBreakpoint: 0,
            }}
            validateEmbeddable={() => true}
          >
            <MainMenu>
              <MainMenu.DefaultItems.LoadScene />
              <MainMenu.DefaultItems.Export />
              <MainMenu.DefaultItems.SaveAsImage />
              <MainMenu.DefaultItems.ClearCanvas />

              <MainMenu.Separator />

              <MainMenu.DefaultItems.ChangeCanvasBackground />
            </MainMenu>

            <Sidebar name="custom" docked={docked} onDock={setDocked}>
              <Sidebar.Header />

              <Sidebar.Tabs
                style={{
                  padding: "0.5rem",
                }}
              >
                <Sidebar.Tab tab="one">Tab One!</Sidebar.Tab>

                <Sidebar.Tab tab="two">Tab Two!</Sidebar.Tab>

                <Sidebar.TabTriggers>
                  <Sidebar.TabTrigger tab="one">One</Sidebar.TabTrigger>

                  <Sidebar.TabTrigger tab="two">Two</Sidebar.TabTrigger>
                </Sidebar.TabTriggers>
              </Sidebar.Tabs>
            </Sidebar>

            <Footer>
              <div
                style={{
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "5px",
                }}
              >
                <button
                  className={
                    liveMode
                      ? "concept_tab-header-active"
                      : "concept_tab-header-nonactive"
                  }
                  onClick={handleCheckEntitlement}
                >
                  {liveMode ? (
                    <IoSyncCircleSharp color="#1a82c4" />
                  ) : (
                    <IoSyncCircleOutline />
                  )}
                  {"  "}
                  Live Mode
                </button>
              </div>
            </Footer>
          </Excalidraw>
        </div>
      </div>

      {isActive && showInvitation && (
        <InvitetoPremium
          setShowInvitation={setShowInvitation}
          featureName="Live MindMap switch"
          featureDesc="brainstorm / realtime sync minds together"
          featureImg="https://images.pexels.com/photos/3758105/pexels-photo-3758105.jpeg"
          currentUser={currentUser}
        />
      )}

      {isActive && <Outlet />}
      {isActive && <AddButton />}
    </div>
  );
};

export default ConceptView;
